// lib/accord-nombres.js — L'ACCORD DES NOMBRES, UNE SEULE FOIS POUR TOUT LE MONDE.
//
// ══ LE DÉFAUT QUI A DONNÉ CE FICHIER (Dimitri, 2026-09-16) ═════════════════
//
//   En prod, l'opérateur a écrit : « 1 choses », « 1 urgentes », « 0 qui ne
//   pressent pas ». Personne ne parle comme ça.
//
//   Et la cause n'était pas à l'écran : elle était dans les RELEVÉS qu'on
//   injecte dans le prompt. Le modèle recopie la forme qu'on lui sert —
//   « 1 courriels attendent » en entrée donne « 1 urgentes » en sortie.
//   Corriger l'affichage seul n'aurait réparé que la moitié visible.
//
// ══ LA CONVENTION ══════════════════════════════════════════════════════════
//
//   En français, le pluriel commence à DEUX. Zéro et un se disent au
//   singulier, et le verbe suit le même seuil :
//
//     0 → « 0 nuit louée »   · « 0 courriel attend »
//     1 → « 1 nuit louée »   · « 1 courriel attend »
//     2 → « 2 nuits louées » · « 2 courriels attendent »
//
//   Le NOM et le VERBE s'accordent ensemble, jamais l'un sans l'autre : c'est
//   exactement le défaut vu en prod (nom accordé, verbe figé au pluriel).
//
//   Les formes « logement(s) », « autre(s) », « ménage(s) » sont bannies :
//   c'est un contournement administratif, pas un accord. On parle comme
//   Dimitri parle.
//
// ══ CE QUE LE HELPER FAIT TOUT SEUL, ET CE QU'IL FAUT LUI DIRE ═════════════
//
//   Pluriel régulier deviné : « courriel » → « courriels », « journal » →
//   « journaux », « niveau » → « niveaux », « mois » → « mois ».
//   Groupes de mots : chaque mot s'accorde JUSQU'À la première préposition,
//   puis plus rien — « nuit louée » → « nuits louées », mais « ménage sans
//   agent » → « ménages sans agent ».
//   Tout pluriel irrégulier hors de ces règles se passe en clair (`pluriel`).
//
//   Verbes : table d'irréguliers (est→sont, a→ont…) puis règle par la
//   terminaison. Dans le doute, passer `verbePluriel` en clair.
//
// ⚠️ Miroir OCTET-IDENTIQUE obligatoire dans `src/lib/accord-nombres.js`
//    (CRA/webpack refuse un import hors de `src/` ; Jest, lui, l'accepte —
//    d'où le risque de tests verts sur un build cassé). Parité gardée par
//    `src/lib/accord-nombres-parite.test.js`.

// Après l'une de ces prépositions, le complément ne compte plus : il ne prend
// pas la marque du nombre (« ménages sans agent », « réponses en attente »).
const PREPOSITIONS = new Set([
  "sans",
  "de",
  "des",
  "du",
  "d'",
  "à",
  "au",
  "aux",
  "en",
  "sur",
  "par",
  "pour",
  "avec",
  "dans",
  "chez",
  "hors",
]);

// Ceux-là ne prennent jamais la marque, mais ne coupent pas la chaîne :
// « 2 non faits », « 2 encore ouverts ».
const INVARIABLES = new Set([
  "non",
  "plus",
  "encore",
  "déjà",
  "jamais",
  "très",
]);

const VERBES_IRREGULIERS = {
  est: "sont",
  a: "ont",
  va: "vont",
  fait: "font",
  peut: "peuvent",
  doit: "doivent",
  vient: "viennent",
  tient: "tiennent",
  sait: "savent",
  dit: "disent",
  prend: "prennent",
  met: "mettent",
  reçoit: "reçoivent",
  vaut: "valent",
  veut: "veulent",
};

/** Pluriel d'un mot simple. Rend le mot tel quel s'il est déjà invariable. */
function plurielMot(mot) {
  if (typeof mot !== "string" || !mot) return mot;
  if (/[sxz]$/i.test(mot)) return mot;
  if (/(au|eau|eu)$/i.test(mot)) return `${mot}x`;
  if (/al$/i.test(mot)) return `${mot.slice(0, -2)}aux`;
  return `${mot}s`;
}

/** Pluriel d'un nom, éventuellement composé de plusieurs mots. */
function plurielNom(nom) {
  if (typeof nom !== "string" || !nom) return nom;
  const mots = nom.split(" ");
  let stop = false;
  return mots
    .map((mot) => {
      if (stop) return mot;
      if (PREPOSITIONS.has(mot.toLowerCase())) {
        stop = true;
        return mot;
      }
      if (INVARIABLES.has(mot.toLowerCase())) return mot;
      return plurielMot(mot);
    })
    .join(" ");
}

/** Pluriel d'un verbe à la 3ᵉ personne. */
function plurielVerbe(verbe) {
  if (typeof verbe !== "string" || !verbe) return verbe;
  const irregulier = VERBES_IRREGULIERS[verbe.toLowerCase()];
  if (irregulier) return irregulier;
  // Imparfait / conditionnel : « avait » → « avaient », « serait » → « seraient ».
  if (/ait$/i.test(verbe)) return `${verbe.slice(0, -1)}ent`;
  if (/e$/i.test(verbe)) return `${verbe}nt`;
  if (/[dt]$/i.test(verbe)) return `${verbe}ent`;
  return `${verbe}nt`;
}

/** Vrai à partir de deux : c'est là que le pluriel français commence. */
function estPluriel(n) {
  const k = Number(n);
  return Number.isFinite(k) && Math.abs(k) >= 2;
}

/** Le nom seul, accordé — sans le nombre devant. */
function nomAccorde(n, nom, pluriel) {
  return estPluriel(n) ? pluriel || plurielNom(nom) : nom;
}

/** Le verbe seul, accordé — pour les phrases où il est loin du nombre. */
function verbeAccorde(n, verbe, pluriel) {
  return estPluriel(n) ? pluriel || plurielVerbe(verbe) : verbe;
}

/**
 * « 2 courriels attendent », « 1 courriel attend », « 0 courriel attend ».
 *
 *   accord(n, "courriel")
 *   accord(n, "courriel", { verbe: "attend" })
 *   accord(n, "nuit louée")                      → « 2 nuits louées »
 *   accord(n, "ménage sans agent")               → « 2 ménages sans agent »
 *   accord(n, "œil", { pluriel: "yeux" })
 *   accord(0, "ménage", { zero: "aucun ménage" })
 */
function accord(n, nom, options) {
  const opts = options || {};
  const brut = Number(n);
  const k = Number.isFinite(brut) ? brut : 0;
  if (k === 0 && typeof opts.zero === "string" && opts.zero) return opts.zero;
  const mot = nomAccorde(k, nom, opts.pluriel);
  const verbe = opts.verbe
    ? ` ${verbeAccorde(k, opts.verbe, opts.verbePluriel)}`
    : "";
  return `${k} ${mot}${verbe}`;
}

module.exports = {
  accord,
  nomAccorde,
  verbeAccorde,
  estPluriel,
  plurielNom,
  plurielVerbe,
};
